import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Table, Tag, Button, Space, Typography, Card, message, Tooltip, Popconfirm, Flex, Modal, Descriptions } from 'antd';
import { 
    ReloadOutlined, 
    DeleteOutlined, 
    CheckCircleOutlined, 
    ClockCircleOutlined, 
    ExclamationCircleOutlined,
    EyeOutlined,
    ExportOutlined,
    InfoCircleOutlined
} from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { notificationsApi, type Notification } from '../../api/notifications';
import { sanitizeHtml } from '../../utils/sanitize';
import dayjs from 'dayjs';

const { Title, Text } = Typography;

export interface NotificationTarget {
    url: string;
    label: string;
}

export const getNotificationTarget = (notification: Notification): NotificationTarget | null => {
    const typeStr = notification.type !== undefined ? notification.type.toString() : '';
    const title = (notification.title || '').toLowerCase();

    // 1. OLE Contact Message
    if (title.includes('ole') && (title.includes('iletişim') || title.includes('iletisim') || title.includes('contact') || title.includes('mesaj'))) {
        return { url: '/admin/ole/contact-messages', label: 'OLE İletişim Mesajları' };
    }

    // 2. Standard Contact Message (NotificationType.ContactMessage = 1)
    if (
        typeStr === '1' || 
        typeStr.toLowerCase() === 'contactmessage' || 
        title.includes('contact message') || 
        title.includes('kontaktanfrage') || 
        title.includes('iletişim') ||
        title.includes('iletisim')
    ) {
        return { url: '/admin/contact-messages', label: 'İletişim Mesajları' };
    }

    // 3. Donation (NotificationType.Donation = 3)
    if (
        typeStr === '3' || 
        typeStr.toLowerCase() === 'donation' || 
        title.includes('donation') || 
        title.includes('spende')
    ) {
        return { url: '/admin/donations', label: 'Bağışlar' };
    }

    // 4. Form Submission (NotificationType.FormSubmission = 2)
    if (
        typeStr === '2' || 
        typeStr.toLowerCase() === 'formsubmission' || 
        title.includes('form submission') || 
        title.includes('formular') ||
        title.includes('form başvuru')
    ) {
        return { url: '/admin/forms', label: 'Formlar & Başvurular' };
    }

    // 5. System Alert (NotificationType.SystemAlert = 4)
    if (
        typeStr === '4' || 
        typeStr.toLowerCase() === 'systemalert' || 
        title.includes('error alert') || 
        title.includes('system alert') || 
        title.includes('hata')
    ) {
        return { url: '/admin/logs', label: 'Sistem Logları' };
    }

    return null;
};

export const NotificationsList: React.FC = () => {
    const navigate = useNavigate();
    const [page, setPage] = useState(1);
    const [pageSize] = useState(10);
    const [viewNotification, setViewNotification] = useState<Notification | null>(null);
    const queryClient = useQueryClient();

    const { data: response, isLoading } = useQuery({
        queryKey: ['notifications', page, pageSize],
        queryFn: () => notificationsApi.getNotifications({ pageNumber: page, pageSize: pageSize })
    });

    const retryMutation = useMutation({
        mutationFn: (id: string) => notificationsApi.retryNotification(id),
        onSuccess: () => {
            message.success('Notification queued for retry');
            queryClient.invalidateQueries({ queryKey: ['notifications'] });
        }
    });

    const deleteMutation = useMutation({
        mutationFn: (id: string) => notificationsApi.deleteNotification(id),
        onSuccess: () => {
            message.success('Notification deleted');
            queryClient.invalidateQueries({ queryKey: ['notifications'] });
        }
    });

    const getStatusTag = (processedAt?: string, error?: string) => {
        if (error) return <Tag color="error" icon={<ExclamationCircleOutlined />}>FAILED</Tag>;
        if (processedAt) return <Tag color="success" icon={<CheckCircleOutlined />}>PROCESSED</Tag>;
        return <Tag color="processing" icon={<ClockCircleOutlined />}>PENDING</Tag>;
    };

    const getPriorityTag = (priority: any) => {
        const colors: Record<string, string> = {
            'Low': 'default',
            'Normal': 'blue',
            'High': 'orange',
            'Critical': 'red',
            // Handle numeric enums if they come as numbers
            '0': 'default',
            '1': 'blue',
            '2': 'orange',
            '3': 'red'
        };

        const priorityStr = priority?.toString() || 'Normal';
        const label = isNaN(Number(priority)) 
            ? priorityStr.toUpperCase() 
            : ['LOW', 'NORMAL', 'HIGH', 'CRITICAL'][Number(priority)] || 'NORMAL';

        return <Tag color={colors[priorityStr] || 'blue'}>{label}</Tag>;
    };

    const columns = [
        {
            title: 'Created At',
            dataIndex: 'createdAt',
            key: 'createdAt',
            render: (date: string) => dayjs(date).format('DD.MM.YYYY HH:mm'),
            width: 150
        },
        {
            title: 'Title',
            dataIndex: 'title',
            key: 'title',
            render: (text: string, record: Notification) => {
                const target = getNotificationTarget(record);
                if (target) {
                    return (
                        <Tooltip title={`${target.label} sayfasına git`}>
                            <a 
                                onClick={() => navigate(target.url)} 
                                style={{ 
                                    fontWeight: 600, 
                                    color: '#1677ff', 
                                    display: 'inline-flex', 
                                    alignItems: 'center', 
                                    gap: 6,
                                    cursor: 'pointer' 
                                }}
                            >
                                {text}
                                <ExportOutlined style={{ fontSize: 12 }} />
                            </a>
                        </Tooltip>
                    );
                }
                return <Text strong>{text}</Text>;
            }
        },
        {
            title: 'Priority',
            dataIndex: 'priority',
            key: 'priority',
            render: (priority: string) => getPriorityTag(priority),
            width: 100
        },
        {
            title: 'Status',
            key: 'status',
            render: (_: any, record: Notification) => getStatusTag(record.processedAt, record.error),
            width: 120
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_: any, record: Notification) => {
                const target = getNotificationTarget(record);
                return (
                    <Space size="small">
                        <Tooltip title={target ? `Detay (${target.label} sayfasına git)` : "Detayları Görüntüle"}>
                            <Button 
                                type={target ? "primary" : "default"}
                                ghost={!!target}
                                icon={target ? <ExportOutlined /> : <EyeOutlined />} 
                                onClick={() => {
                                    if (target) {
                                        navigate(target.url);
                                    } else {
                                        setViewNotification(record);
                                    }
                                }}
                            />
                        </Tooltip>
                        {target && (
                            <Tooltip title="Ham Mesaj / Kuyruk Detayı">
                                <Button 
                                    icon={<InfoCircleOutlined />} 
                                    onClick={() => setViewNotification(record)}
                                />
                            </Tooltip>
                        )}
                        <Tooltip title="Tekrar Kuyruğa Al">
                            <Popconfirm 
                                title="Bu bildirimi tekrar kuyruğa alıp göndermek istiyor musunuz?" 
                                onConfirm={() => retryMutation.mutate(record.id)}
                                okText="Evet"
                                cancelText="Hayır"
                            >
                                <Button 
                                    icon={<ReloadOutlined />} 
                                    loading={retryMutation.isPending && retryMutation.variables === record.id} 
                                />
                            </Popconfirm>
                        </Tooltip>
                        <Popconfirm 
                            title="Delete notification?" 
                            onConfirm={() => deleteMutation.mutate(record.id)}
                            okText="Yes" 
                            cancelText="No"
                        >
                            <Button danger icon={<DeleteOutlined />} loading={deleteMutation.isPending} />
                        </Popconfirm>
                    </Space>
                );
            },
            width: 180
        }
    ];

    return (
        <Space orientation="vertical" size="large" style={{ width: '100%' }}>
            <Flex justify="space-between" align="center">
                <div>
                    <Title level={2} style={{ marginBottom: 0 }}>Notification Queue</Title>
                    <Text type="secondary">Monitor and manage administrative alerts.</Text>
                </div>
                <Button 
                    icon={<ReloadOutlined />} 
                    onClick={() => queryClient.invalidateQueries({ queryKey: ['notifications'] })}
                >
                    Refresh
                </Button>
            </Flex>

            <Card variant="borderless">
                <Table 
                    columns={columns} 
                    dataSource={response?.data || []} 
                    loading={isLoading}
                    rowKey="id"
                    pagination={{
                        current: page,
                        pageSize: pageSize,
                        total: response?.totalRecords || 0,
                        onChange: (p) => setPage(p)
                    }}
                    expandable={{
                        expandedRowRender: (record) => (
                            <div style={{ margin: 0 }}>
                                <p><strong>Recipient:</strong> {record.recipient || 'N/A'}</p>
                                <p><strong>Message:</strong></p>
                                <pre style={{ background: '#f5f5f5', padding: '10px', borderRadius: '4px' }}>
                                    {record.message}
                                </pre>
                                {record.error && (
                                    <p style={{ color: '#ff4d4f' }}><strong>Error:</strong> {record.error}</p>
                                )}
                            </div>
                        )
                    }}
                />
            </Card>

            <Modal
                title={viewNotification?.title || "Bildirim Detayı"}
                open={!!viewNotification}
                onCancel={() => setViewNotification(null)}
                footer={[
                    viewNotification && getNotificationTarget(viewNotification) && (
                        <Button 
                            key="go-to-data" 
                            type="primary" 
                            icon={<ExportOutlined />} 
                            onClick={() => {
                                const target = getNotificationTarget(viewNotification);
                                if (target) {
                                    setViewNotification(null);
                                    navigate(target.url);
                                }
                            }}
                        >
                            {getNotificationTarget(viewNotification)?.label} Sayfasına Git
                        </Button>
                    ),
                    <Button key="close" onClick={() => setViewNotification(null)}>
                        Kapat
                    </Button>
                ].filter(Boolean)}
                width={750}
            >
                {viewNotification && (() => {
                    const target = getNotificationTarget(viewNotification);
                    return (
                        <div style={{ maxHeight: '70vh', overflowY: 'auto', paddingRight: 4 }}>
                            <Descriptions column={2} bordered size="small" style={{ marginBottom: 16 }}>
                                <Descriptions.Item label="Öncelik">{getPriorityTag(viewNotification.priority)}</Descriptions.Item>
                                <Descriptions.Item label="Tarih">{dayjs(viewNotification.createdAt).format('DD.MM.YYYY HH:mm:ss')}</Descriptions.Item>
                                <Descriptions.Item label="Alıcı">{viewNotification.recipient || 'Varsayılan'}</Descriptions.Item>
                                <Descriptions.Item label="Durum">{getStatusTag(viewNotification.processedAt, viewNotification.error)}</Descriptions.Item>
                                {target && (
                                    <Descriptions.Item label="İlgili Veri / Sayfa" span={2}>
                                        <Button 
                                            type="link" 
                                            icon={<ExportOutlined />} 
                                            onClick={() => {
                                                setViewNotification(null);
                                                navigate(target.url);
                                            }}
                                            style={{ padding: 0, fontWeight: 600 }}
                                        >
                                            {target.label} ({target.url})
                                        </Button>
                                    </Descriptions.Item>
                                )}
                            </Descriptions>
                            <div style={{ marginTop: 12 }}>
                                <Text strong style={{ display: 'block', marginBottom: 8 }}>Bildirim İçeriği:</Text>
                                {viewNotification.message.includes('<') ? (
                                    <div 
                                        dangerouslySetInnerHTML={{ __html: sanitizeHtml(viewNotification.message) }} 
                                        style={{ border: '1px solid #e2e8f0', borderRadius: 8, padding: 16, background: '#fff' }}
                                    />
                                ) : (
                                    <pre style={{ whiteSpace: 'pre-wrap', background: '#f8fafc', padding: 12, borderRadius: 6, fontSize: 13, border: '1px solid #e2e8f0' }}>
                                        {viewNotification.message}
                                    </pre>
                                )}
                            </div>
                        </div>
                    );
                })()}
            </Modal>
        </Space>
    );
};
